import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { API, useAuth } from '../../App';
import AdminLayout from '../../components/AdminLayout';
import { UserStatsCards } from '../../components/admin/UserStatsCards';
import { toast } from 'sonner';
import {
  Search,
  Crown,
  Zap,
  Star,
  Gift,
  ChevronLeft,
  ChevronRight,
  Loader2,
  Shield,
  UserPlus,
  Trash2,
  KeyRound,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';

const AdminUsers = () => {
  const { token } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterPlan, setFilterPlan] = useState('all');
  const [page, setPage] = useState(1);
  const [grantDialogOpen, setGrantDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [grantPlan, setGrantPlan] = useState('basic');
  const [grantDays, setGrantDays] = useState(30);
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState({ email: '', name: '', password: '', is_admin: false, plan_id: 'none', plan_days: 30, credits: 0 });
  const [createBusy, setCreateBusy] = useState(false);
  const [pwdOpen, setPwdOpen] = useState(false);
  const [pwdForm, setPwdForm] = useState({ current_password: '', new_password: '', confirm: '' });
  const [pwdBusy, setPwdBusy] = useState(false);
  const itemsPerPage = 15;

  const fetchUsers = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(res.data.users || []);
    } catch (e) {
      toast.error('Error al cargar usuarios');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const openGrantDialog = (user) => {
    setSelectedUser(user);
    setGrantPlan('basic');
    setGrantDays(30);
    setGrantDialogOpen(true);
  };

  const handleGrantSubscription = async () => {
    try {
      await axios.post(
        `${API}/admin/users/${selectedUser.id}/grant-subscription`,
        { plan_id: grantPlan, days: grantDays },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Suscripción ${grantPlan} otorgada a ${selectedUser.email}`);
      setGrantDialogOpen(false);
      fetchUsers();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al otorgar suscripción');
    }
  };

  const handleToggleAdmin = async (user) => {
    if (!window.confirm(`¿${user.is_admin ? 'Quitar' : 'Dar'} permisos de admin a ${user.email}?`)) return;
    
    try {
      await axios.post(
        `${API}/admin/users/${user.id}/toggle-admin`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Permisos actualizados para ${user.email}`);
      fetchUsers();
    } catch (e) {
      toast.error('Error al actualizar permisos');
    }
  };

  const handleCreateUser = async () => {
    if (!createForm.email.trim() || !createForm.password) {
      toast.error('Email y contraseña son obligatorios');
      return;
    }
    if (createForm.password.length < 6) {
      toast.error('La contraseña debe tener al menos 6 caracteres');
      return;
    }
    setCreateBusy(true);
    try {
      const payload = {
        email: createForm.email,
        name: createForm.name,
        password: createForm.password,
        is_admin: createForm.is_admin,
        plan_id: createForm.plan_id === 'none' ? null : createForm.plan_id,
        plan_days: Number(createForm.plan_days) || 30,
        credits: Number(createForm.credits) || 0,
      };
      await axios.post(
        `${API}/admin/users`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Usuario ${createForm.email} creado`);
      setCreateOpen(false);
      setCreateForm({ email: '', name: '', password: '', is_admin: false, plan_id: 'none', plan_days: 30, credits: 0 });
      fetchUsers();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al crear usuario');
    } finally {
      setCreateBusy(false);
    }
  };

  const handleDeleteUser = async (user) => {
    if (!window.confirm(`¿Eliminar definitivamente a ${user.email}? Esta acción no se puede deshacer.`)) return;
    try {
      await axios.delete(
        `${API}/admin/users/${user.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Usuario ${user.email} eliminado`);
      fetchUsers();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al eliminar usuario');
    }
  };

  const handleChangePassword = async () => {
    if (!pwdForm.current_password || !pwdForm.new_password) {
      toast.error('Completa todos los campos');
      return;
    }
    if (pwdForm.new_password.length < 6) {
      toast.error('La nueva contraseña debe tener al menos 6 caracteres');
      return;
    }
    if (pwdForm.new_password !== pwdForm.confirm) {
      toast.error('Las contraseñas nuevas no coinciden');
      return;
    }
    setPwdBusy(true);
    try {
      await axios.post(
        `${API}/admin/change-password`,
        { current_password: pwdForm.current_password, new_password: pwdForm.new_password },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Contraseña actualizada correctamente');
      setPwdOpen(false);
      setPwdForm({ current_password: '', new_password: '', confirm: '' });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al cambiar la contraseña');
    } finally {
      setPwdBusy(false);
    }
  };

  const getPlanBadge = (subscription) => {
    if (!subscription) {
      return <Badge variant="outline" className="text-gray-500 border-gray-600">Sin Plan</Badge>;
    }
    
    const planColors = {
      vip: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
      premium: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
      basic: 'bg-blue-500/20 text-blue-400 border-blue-500/30'
    };
    
    const planIcons = {
      vip: <Crown size={12} className="mr-1" />,
      premium: <Zap size={12} className="mr-1" />,
      basic: <Star size={12} className="mr-1" />
    };
    
    return (
      <Badge className={planColors[subscription.plan_id] || planColors.basic}>
        {planIcons[subscription.plan_id]}
        {subscription.plan_id?.toUpperCase()}
      </Badge>
    );
  };

  const filteredUsers = useMemo(() => users.filter(user => {
    const matchesSearch = user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         user.name?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesPlan = filterPlan === 'all' || 
                       (filterPlan === 'none' && !user.subscription) ||
                       (user.subscription?.plan_id === filterPlan);
    return matchesSearch && matchesPlan;
  }), [users, searchTerm, filterPlan]);

  const paginatedUsers = useMemo(() => filteredUsers.slice(
    (page - 1) * itemsPerPage,
    page * itemsPerPage
  ), [filteredUsers, page, itemsPerPage]);

  const totalPages = Math.ceil(filteredUsers.length / itemsPerPage);

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('es-DO', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <AdminLayout title="Gestión de Usuarios" subtitle="Cargando...">
        <div className="flex items-center justify-center h-64">
          <Loader2 size={40} className="text-amber-400 animate-spin" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Gestion de Usuarios" subtitle={`${users.length} usuarios registrados`}>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-end gap-3 mb-4">
        <Button
          onClick={() => setPwdOpen(true)}
          variant="outline"
          className="border-gray-700 text-gray-200 hover:bg-gray-800"
          data-testid="open-change-password-btn"
        >
          <KeyRound size={16} className="mr-2" />
          Cambiar mi contraseña
        </Button>
        <Button
          onClick={() => setCreateOpen(true)}
          className="bg-amber-600 hover:bg-amber-500 text-white"
          data-testid="open-create-user-btn"
        >
          <UserPlus size={16} className="mr-2" />
          Crear usuario
        </Button>
      </div>

      <UserStatsCards users={users} />

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        <div className="relative flex-1">
          <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
          <Input
            placeholder="Buscar por email o nombre..."
            value={searchTerm}
            onChange={(e) => { setSearchTerm(e.target.value); setPage(1); }}
            className="pl-10 bg-gray-900 border-gray-700 text-white"
            data-testid="users-search"
          />
        </div>
        <Select value={filterPlan} onValueChange={(v) => { setFilterPlan(v); setPage(1); }}>
          <SelectTrigger className="w-[180px] bg-gray-900 border-gray-700 text-white" data-testid="users-plan-filter">
            <SelectValue placeholder="Filtrar por plan" />
          </SelectTrigger>
          <SelectContent className="bg-gray-900 border-gray-700">
            <SelectItem value="all" className="text-white hover:bg-gray-800">Todos</SelectItem>
            <SelectItem value="none" className="text-white hover:bg-gray-800">Sin Plan</SelectItem>
            <SelectItem value="basic" className="text-white hover:bg-gray-800">Básico</SelectItem>
            <SelectItem value="premium" className="text-white hover:bg-gray-800">Premium</SelectItem>
            <SelectItem value="vip" className="text-white hover:bg-gray-800">VIP</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Users Table */}
      <Card className="bg-gray-900 border-gray-800" data-testid="users-table-card">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="border-gray-800 hover:bg-transparent">
                <TableHead className="text-gray-400">Usuario</TableHead>
                <TableHead className="text-gray-400">Plan</TableHead>
                <TableHead className="text-gray-400">Expira</TableHead>
                <TableHead className="text-gray-400">Registro</TableHead>
                <TableHead className="text-gray-400">Rol</TableHead>
                <TableHead className="text-gray-400 text-right">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {paginatedUsers.map((user) => (
                <TableRow key={user.id} className="border-gray-800 hover:bg-gray-800/50" data-testid={`user-row-${user.id}`}>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-full bg-gradient-to-br from-gray-700 to-gray-800 flex items-center justify-center">
                        <span className="text-white text-sm font-bold">
                          {user.name?.charAt(0) || user.email.charAt(0).toUpperCase()}
                        </span>
                      </div>
                      <div>
                        <p className="text-white font-medium">{user.name || 'Sin nombre'}</p>
                        <p className="text-gray-500 text-xs">{user.email}</p>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>{getPlanBadge(user.subscription)}</TableCell>
                  <TableCell className="text-gray-400 text-sm">
                    {user.subscription ? formatDate(user.subscription.end_date) : '-'}
                  </TableCell>
                  <TableCell className="text-gray-400 text-sm">
                    {formatDate(user.created_at)}
                  </TableCell>
                  <TableCell>
                    {user.is_admin ? (
                      <Badge className="bg-red-500/20 text-red-400 border-red-500/30">
                        <Shield size={12} className="mr-1" />
                        Admin
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="text-gray-500 border-gray-600">
                        Usuario
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openGrantDialog(user)}
                        className="text-green-400 hover:text-green-300 hover:bg-green-500/10"
                        title="Otorgar suscripción"
                        data-testid={`grant-${user.id}`}
                      >
                        <Gift size={16} />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleToggleAdmin(user)}
                        className={user.is_admin ? 'text-red-400 hover:text-red-300 hover:bg-red-500/10' : 'text-amber-400 hover:text-amber-300 hover:bg-amber-500/10'}
                        title={user.is_admin ? 'Quitar admin' : 'Hacer admin'}
                        data-testid={`toggle-admin-${user.id}`}
                      >
                        <Shield size={16} />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteUser(user)}
                        className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                        title="Eliminar usuario"
                        data-testid={`delete-user-${user.id}`}
                      >
                        <Trash2 size={16} />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-4 mt-6">
          <Button
            variant="outline"
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page === 1}
            className="border-gray-700 text-gray-300 hover:bg-gray-800"
          >
            <ChevronLeft size={18} />
          </Button>
          <span className="text-gray-400">
            Página <span className="text-white font-mono">{page}</span> de{' '}
            <span className="text-white font-mono">{totalPages}</span>
          </span>
          <Button
            variant="outline"
            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
            className="border-gray-700 text-gray-300 hover:bg-gray-800"
          >
            <ChevronRight size={18} />
          </Button>
        </div>
      )}

      {/* Grant Subscription Dialog */}
      <Dialog open={grantDialogOpen} onOpenChange={setGrantDialogOpen}>
        <DialogContent className="sm:max-w-md bg-gray-900 border-gray-700" data-testid="grant-dialog">
          <DialogHeader>
            <DialogTitle className="text-white">Otorgar Suscripción</DialogTitle>
            <DialogDescription className="text-gray-400">
              Otorga acceso gratuito a {selectedUser?.email}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label className="text-gray-300">Plan</Label>
              <Select value={grantPlan} onValueChange={setGrantPlan}>
                <SelectTrigger className="w-full bg-gray-800 border-gray-700 text-white" data-testid="grant-plan-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-gray-900 border-gray-700">
                  <SelectItem value="basic" className="text-white hover:bg-gray-800">
                    <div className="flex items-center gap-2">
                      <Star size={14} className="text-blue-400" />
                      Básico ($19.99/mes)
                    </div>
                  </SelectItem>
                  <SelectItem value="premium" className="text-white hover:bg-gray-800">
                    <div className="flex items-center gap-2">
                      <Zap size={14} className="text-purple-400" />
                      Premium ($39.99/mes)
                    </div>
                  </SelectItem>
                  <SelectItem value="vip" className="text-white hover:bg-gray-800">
                    <div className="flex items-center gap-2">
                      <Crown size={14} className="text-amber-400" />
                      VIP ($59.99/mes)
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label className="text-gray-300">Duración (días)</Label>
              <Input
                type="number"
                value={grantDays}
                onChange={(e) => setGrantDays(parseInt(e.target.value))}
                min={1}
                max={365}
                className="bg-gray-800 border-gray-700 text-white"
                data-testid="grant-days-input"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => setGrantDialogOpen(false)} className="text-gray-400">
              Cancelar
            </Button>
            <Button onClick={handleGrantSubscription} className="bg-green-600 hover:bg-green-500" data-testid="grant-confirm-btn">
              <Gift size={16} className="mr-2" />
              Otorgar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create User Dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="sm:max-w-md bg-gray-900 border-gray-700" data-testid="create-user-dialog">
          <DialogHeader>
            <DialogTitle className="text-white">Crear Usuario</DialogTitle>
            <DialogDescription className="text-gray-400">
              Crea una nueva cuenta manualmente.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label className="text-gray-300">Email *</Label>
              <Input
                type="email"
                value={createForm.email}
                onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })}
                placeholder="usuario@correo.com"
                className="bg-gray-800 border-gray-700 text-white"
                data-testid="create-user-email"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-gray-300">Nombre</Label>
              <Input
                value={createForm.name}
                onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })}
                placeholder="Nombre (opcional)"
                className="bg-gray-800 border-gray-700 text-white"
                data-testid="create-user-name"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-gray-300">Contraseña *</Label>
              <Input
                type="password"
                value={createForm.password}
                onChange={(e) => setCreateForm({ ...createForm, password: e.target.value })}
                placeholder="Mínimo 6 caracteres"
                className="bg-gray-800 border-gray-700 text-white"
                data-testid="create-user-password"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label className="text-gray-300">Plan (opcional)</Label>
                <Select value={createForm.plan_id} onValueChange={(v) => setCreateForm({ ...createForm, plan_id: v })}>
                  <SelectTrigger className="w-full bg-gray-800 border-gray-700 text-white" data-testid="create-user-plan">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-gray-900 border-gray-700">
                    <SelectItem value="none" className="text-white hover:bg-gray-800">Sin plan</SelectItem>
                    <SelectItem value="basic" className="text-white hover:bg-gray-800">Básico</SelectItem>
                    <SelectItem value="premium" className="text-white hover:bg-gray-800">Premium</SelectItem>
                    <SelectItem value="vip" className="text-white hover:bg-gray-800">VIP</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-gray-300">Días de plan</Label>
                <Input
                  type="number"
                  min={1}
                  max={365}
                  value={createForm.plan_days}
                  onChange={(e) => setCreateForm({ ...createForm, plan_days: e.target.value })}
                  disabled={createForm.plan_id === 'none'}
                  className="bg-gray-800 border-gray-700 text-white disabled:opacity-40"
                  data-testid="create-user-plan-days"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-gray-300">Créditos iniciales</Label>
              <Input
                type="number"
                min={0}
                value={createForm.credits}
                onChange={(e) => setCreateForm({ ...createForm, credits: e.target.value })}
                placeholder="0"
                className="bg-gray-800 border-gray-700 text-white"
                data-testid="create-user-credits"
              />
            </div>
            <div className="flex items-center justify-between rounded-lg border border-gray-700 bg-gray-800/50 px-3 py-2">
              <div>
                <p className="text-white text-sm font-medium">Permisos de administrador</p>
                <p className="text-gray-500 text-xs">El usuario podrá acceder al panel admin</p>
              </div>
              <Switch
                checked={createForm.is_admin}
                onCheckedChange={(v) => setCreateForm({ ...createForm, is_admin: v })}
                data-testid="create-user-is-admin"
              />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => setCreateOpen(false)} className="text-gray-400">
              Cancelar
            </Button>
            <Button onClick={handleCreateUser} disabled={createBusy} className="bg-amber-600 hover:bg-amber-500" data-testid="create-user-confirm-btn">
              {createBusy ? <Loader2 size={16} className="mr-2 animate-spin" /> : <UserPlus size={16} className="mr-2" />}
              Crear
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Change Password Dialog */}
      <Dialog open={pwdOpen} onOpenChange={setPwdOpen}>
        <DialogContent className="sm:max-w-md bg-gray-900 border-gray-700" data-testid="change-password-dialog">
          <DialogHeader>
            <DialogTitle className="text-white">Cambiar mi contraseña</DialogTitle>
            <DialogDescription className="text-gray-400">
              Actualiza la contraseña de tu cuenta de administrador.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label className="text-gray-300">Contraseña actual</Label>
              <Input
                type="password"
                value={pwdForm.current_password}
                onChange={(e) => setPwdForm({ ...pwdForm, current_password: e.target.value })}
                className="bg-gray-800 border-gray-700 text-white"
                data-testid="current-password-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-gray-300">Nueva contraseña</Label>
              <Input
                type="password"
                value={pwdForm.new_password}
                onChange={(e) => setPwdForm({ ...pwdForm, new_password: e.target.value })}
                placeholder="Mínimo 6 caracteres"
                className="bg-gray-800 border-gray-700 text-white"
                data-testid="new-password-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-gray-300">Confirmar nueva contraseña</Label>
              <Input
                type="password"
                value={pwdForm.confirm}
                onChange={(e) => setPwdForm({ ...pwdForm, confirm: e.target.value })}
                className="bg-gray-800 border-gray-700 text-white"
                data-testid="confirm-password-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => setPwdOpen(false)} className="text-gray-400">
              Cancelar
            </Button>
            <Button onClick={handleChangePassword} disabled={pwdBusy} className="bg-amber-600 hover:bg-amber-500" data-testid="change-password-confirm-btn">
              {pwdBusy ? <Loader2 size={16} className="mr-2 animate-spin" /> : <KeyRound size={16} className="mr-2" />}
              Actualizar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default AdminUsers;
